'use client';

import { useMemo, useRef } from 'react';

import { GearCategory, PlayerGear } from '@/modules/players/v3/types';

/**
 * Orders gear favorites-first, each group sorted by category display order (the same order
 * as the My Gear page category tabs). The order freezes once gear and categories are both
 * available so toggling a favorite doesn't move cards around while the user is interacting.
 * New gear is appended, removed gear simply drops out.
 */
export const useFavoritesFirstOrder = (
  gear: PlayerGear[],
  categories?: GearCategory[]
): PlayerGear[] => {
  const frozenOrder = useRef<string[] | null>(null);

  if (frozenOrder.current === null && gear.length > 0 && categories) {
    const rankByCategoryId = new Map(
      [...categories]
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((category, index) => [category.categoryId, index])
    );

    const categoryRank = (item: PlayerGear) => {
      const ranks = item.product.categories
        .map((categoryId) => rankByCategoryId.get(categoryId))
        .filter((rank): rank is number => rank !== undefined);
      return ranks.length > 0 ? Math.min(...ranks) : Number.MAX_SAFE_INTEGER;
    };

    frozenOrder.current = [...gear]
      .sort(
        (a, b) => Number(b.isFavorite) - Number(a.isFavorite) || categoryRank(a) - categoryRank(b)
      )
      .map((item) => item.uuid);
  }

  return useMemo(() => {
    const order = frozenOrder.current;
    if (!order) {
      return [...gear].sort((a, b) => Number(b.isFavorite) - Number(a.isFavorite));
    }
    const rankByUuid = new Map(order.map((uuid, index) => [uuid, index]));
    return [...gear].sort(
      (a, b) => (rankByUuid.get(a.uuid) ?? order.length) - (rankByUuid.get(b.uuid) ?? order.length)
    );
  }, [gear, categories]);
};
